/**
 * TagService
 *
 * Gerencia tags para produtos na tabela de sellout.
 * As tags sao atribuidas baseadas na primeira coluna (index column) que pode ser:
 * - itemId (MLB - codigo do anuncio)
 * - sku (SKU do produto)
 * - inventoryId (ID do inventario)
 *
 * Armazena tags no localStorage (client-side) por enquanto.
 * Futuramente sera integrado com backend API.
 */
class TagService {
    constructor() {
        this.STORAGE_KEY = 'sellout_tags';
    }

    /**
     * Gera chave de identificacao para uma tag baseada no tipo e valor
     * @param {string} type - Tipo: 'itemId', 'sku', 'inventoryId'
     * @param {string} value - Valor do identificador
     * @returns {string} Chave formatada
     */
    getTagKey(type, value) {
        if (!value) {
            return null;
        }
        return `${type}:${value}`;
    }

    /**
     * Carrega todas as tags do localStorage
     * @returns {Object} Objeto com chaves de tags e arrays de valores
     */
    loadTags() {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (error) {
            console.error('Erro ao carregar tags do localStorage:', error);
        }
        return {};
    }

    /**
     * Salva tags no localStorage
     * @param {Object} tags - Objeto com chaves de tags e arrays de valores
     */
    saveTags(tags) {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tags));
        } catch (error) {
            console.error('Erro ao salvar tags no localStorage:', error);
            throw error;
        }
    }

    /**
     * Adiciona uma tag a um identificador
     * @param {string} type - Tipo: 'itemId', 'sku', 'inventoryId'
     * @param {string} value - Valor do identificador
     * @param {string} tag - Nome da tag a ser adicionada
     */
    addTag(type, value, tag) {
        if (!value || !tag || !tag.trim()) {
            return;
        }

        const tagKey = this.getTagKey(type, value);
        if (!tagKey) {
            return;
        }

        const tags = this.loadTags();
        if (!tags[tagKey]) {
            tags[tagKey] = [];
        }

        const trimmedTag = tag.trim();
        // Evitar duplicatas (case-insensitive)
        const tagExists = tags[tagKey].some((t) => t.toLowerCase() === trimmedTag.toLowerCase());

        if (!tagExists) {
            tags[tagKey].push(trimmedTag);
            this.saveTags(tags);
        }
    }

    /**
     * Remove uma tag de um identificador
     * @param {string} type - Tipo: 'itemId', 'sku', 'inventoryId'
     * @param {string} value - Valor do identificador
     * @param {string} tag - Nome da tag a ser removida
     */
    removeTag(type, value, tag) {
        if (!value || !tag) {
            return;
        }

        const tagKey = this.getTagKey(type, value);
        if (!tagKey) {
            return;
        }

        const tags = this.loadTags();
        if (!tags[tagKey]) {
            return;
        }

        tags[tagKey] = tags[tagKey].filter((t) => t.toLowerCase() !== tag.toLowerCase());

        // Remover chave se nao houver mais tags
        if (tags[tagKey].length === 0) {
            delete tags[tagKey];
        }

        this.saveTags(tags);
    }

    /**
     * Obtem todas as tags de um identificador
     * @param {string} type - Tipo: 'itemId', 'sku', 'inventoryId'
     * @param {string} value - Valor do identificador
     * @returns {Array<string>} Array de tags
     */
    getTags(type, value) {
        if (!value) {
            return [];
        }

        const tagKey = this.getTagKey(type, value);
        if (!tagKey) {
            return [];
        }

        const tags = this.loadTags();
        return tags[tagKey] || [];
    }

    /**
     * Obtem tags para multiplos identificadores (usado quando ha multiplas colunas visiveis)
     * @param {Array<{type: string, value: string}>} identifiers - Array de identificadores
     * @returns {Array<string>} Array unico de tags (sem duplicatas)
     */
    getTagsForMultiple(identifiers) {
        const allTags = new Set();

        identifiers.forEach(({ type, value }) => {
            const tags = this.getTags(type, value);
            tags.forEach((tag) => {
                allTags.add(tag);
            });
        });

        return Array.from(allTags);
    }

    /**
     * Obtem todos os identificadores que possuem uma tag especifica
     * @param {string} tag - Nome da tag
     * @returns {Array<{type: string, value: string}>} Array de identificadores
     */
    getIdentifiersByTag(tag) {
        if (!tag) {
            return [];
        }

        const tags = this.loadTags();
        const identifiers = [];

        Object.keys(tags).forEach((key) => {
            const hasTag = tags[key].some((t) => t.toLowerCase() === tag.toLowerCase());
            if (hasTag) {
                const [type, ...valueParts] = key.split(':');
                const value = valueParts.join(':'); // Caso o valor tenha ':'
                identifiers.push({ type, value });
            }
        });

        return identifiers;
    }

    /**
     * Obtem todas as tags unicas do sistema
     * @returns {Array<string>} Array de todas as tags unicas
     */
    getAllTags() {
        const tags = this.loadTags();
        const allTags = new Set();

        Object.values(tags).forEach((tagArray) => {
            tagArray.forEach((tag) => {
                allTags.add(tag);
            });
        });

        return Array.from(allTags).sort();
    }

    /**
     * Filtra produtos baseado em tags
     * @param {Array} products - Array de produtos
     * @param {Array<string>} selectedTags - Array de tags selecionadas para filtrar
     * @param {Array} visibleColumns - Colunas visiveis na tabela
     * @param {Function} extractSellerSku - Funcao para extrair SKU do produto
     * @returns {Array} Array de produtos filtrados
     */
    filterProductsByTags(products, selectedTags, visibleColumns, extractSellerSku) {
        if (!selectedTags || selectedTags.length === 0) {
            return products;
        }

        // Identificar quais tipos de identificadores estao visiveis
        const visibleIdentifierTypes = [];
        visibleColumns.forEach((column) => {
            if (column.type === 'predefined') {
                if (column.key === 'title' || column.key === 'itemId') {
                    visibleIdentifierTypes.push('itemId');
                } else if (column.key === 'sku') {
                    visibleIdentifierTypes.push('sku');
                } else if (column.key === 'inventoryId') {
                    visibleIdentifierTypes.push('inventoryId');
                }
            }
        });

        // Remover duplicatas
        const uniqueTypes = [...new Set(visibleIdentifierTypes)];

        return products.filter((product) => {
            // Verificar se o produto tem pelo menos uma das tags selecionadas
            // em qualquer um dos identificadores visiveis
            return uniqueTypes.some((type) => {
                let value = null;

                if (type === 'itemId') {
                    value = product.itemId || product.item_id;
                } else if (type === 'sku') {
                    value = extractSellerSku(product);
                } else if (type === 'inventoryId') {
                    value = product.inventoryId;
                }

                if (!value) {
                    return false;
                }

                const productTags = this.getTags(type, value);
                return selectedTags.some((selectedTag) =>
                    productTags.some((tag) => tag.toLowerCase() === selectedTag.toLowerCase()),
                );
            });
        });
    }
}

// Instancia singleton
const tagService = new TagService();

export default tagService;
